import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Save, X } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAllowancePeriod, useUpdateAllowancePeriod } from "@/hooks/use-allowance-period";
import { formatDate } from "@/lib/format";
import {
  type AllowancePeriodFormValues,
  allowancePeriodFormSchema,
} from "@/schemas/allowance-period";

/**
 * Single view/edit-toggle card for the Allowance Period singleton — no
 * dialog, no list, no detail route (GET + PUT only, see
 * .claude/rules/api.md note in src/api/allowance-period.ts). Lives on
 * Home/Today per the shell's IA (no dedicated Allowance tab).
 */
export function AllowancePeriodCard() {
  const { data: period, isLoading, isError, error, refetch } = useAllowancePeriod();
  const updatePeriod = useUpdateAllowancePeriod();
  const [editing, setEditing] = useState(false);

  const form = useForm<AllowancePeriodFormValues>({
    resolver: zodResolver(allowancePeriodFormSchema),
    values: period
      ? {
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          totalAllowanceHours: period.totalAllowanceHours,
        }
      : undefined,
  });

  function handleSubmit(values: AllowancePeriodFormValues) {
    updatePeriod.mutate(values, { onSuccess: () => setEditing(false) });
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !period) {
    return (
      <Card>
        <CardContent className="py-6">
          <ErrorState
            message={error?.message ?? "Failed to load allowance"}
            onRetry={() => refetch()}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Allowance</CardTitle>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="periodStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Period start</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="periodEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Period end</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="totalAllowanceHours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Total allowance (hours)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        name={field.name}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                        value={Number.isNaN(field.value) ? "" : field.value}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditing(false);
                    form.reset();
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={updatePeriod.isPending}>
                  <Save className="h-4 w-4 mr-1" />
                  {updatePeriod.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Period</span>
              <p className="mt-1">
                {formatDate(period.periodStart)} – {formatDate(period.periodEnd)}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Total allowance</span>
              <p className="mt-1">{period.totalAllowanceHours} hours</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
